import { useEffect, useMemo, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { useAuth } from '../auth/authContext.js';
import { PageHeader } from '../components/layout/PageHeader.js';
import { Badge } from '../components/ui/Badge.js';
import { DataState } from '../components/ui/DataState.js';
import { formatDate } from '../utils/date.js';
import { useMemberOptions } from '../features/members/api.js';
import { usePreloadCurrentWeek, useTimesheets } from '../features/timesheets/api.js';
import { STATUS_LABELS, STATUS_TONES } from '../features/timesheets/status.js';
import { useTickets } from '../features/tickets/api.js';
import { ticketDisplayId } from '../features/tickets/reference.js';
import { useProjects } from '../features/projects/api.js';
import {
  OPPORTUNITY_STAGE_LABELS,
  OPPORTUNITY_STAGE_TONES,
  OPPORTUNITY_STATUS_LABELS,
} from '../features/opportunities/status.js';
import { useOpportunities } from '../features/opportunities/api.js';

/** Charge-code tickets all live under this project; they are hidden from the dashboard. */
const CHARGE_CODE_PROJECT_NUMBER = 461;

/** Card shell used by every dashboard section. */
function SectionCard({
  title,
  description,
  action,
  className,
  children,
}: {
  title: string;
  description?: string;
  action?: JSX.Element;
  className?: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <section
      className={`overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm ${className ?? ''}`}
    >
      <div className="flex items-start justify-between gap-3 border-b border-slate-200 px-4 py-3">
        <div>
          <h2 className="text-sm font-semibold text-slate-900">{title}</h2>
          {description ? <p className="mt-0.5 text-xs text-slate-500">{description}</p> : null}
        </div>
        {action}
      </div>
      <div className="p-4">{children}</div>
    </section>
  );
}

/** The current member's most recent draft timesheet, with a link to open it. */
function MyTimesheetCard({ memberId }: { memberId: string }): JSX.Element {
  // Home is often the first page opened in a new week, so ensure that week's draft exists
  // (the timesheets page does the same) instead of showing an empty card.
  const preloadMutation = usePreloadCurrentWeek();
  const didPreload = useRef(false);
  const preload = preloadMutation.mutate;
  useEffect(() => {
    if (didPreload.current) return;
    didPreload.current = true;
    preload();
  }, [preload]);

  const query = useTimesheets({
    page: 1,
    pageSize: 1,
    memberId,
    status: 'draft',
    sortBy: 'weekStartDate',
    sortDir: 'desc',
  });
  const timesheet = query.data?.data[0] ?? null;

  return (
    <SectionCard
      title="My current timesheet"
      description="Your most recent draft week"
      className="lg:col-span-2"
      action={
        <Link to="/timesheets" className="text-xs font-medium text-brand-600 hover:text-brand-800">
          All timesheets
        </Link>
      }
    >
      {query.isLoading || preloadMutation.isPending ? (
        <p className="text-sm text-slate-500">Loading…</p>
      ) : query.isError ? (
        <p className="text-sm text-rose-600">Could not load your timesheet.</p>
      ) : !timesheet ? (
        <p className="text-sm text-slate-500">You have no draft timesheet right now.</p>
      ) : (
        <Link
          to={`/timesheets/${timesheet.id}`}
          className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-slate-100 bg-slate-50 px-4 py-3 hover:bg-slate-100"
        >
          <div>
            <p className="text-sm font-medium text-slate-900">
              Week of {formatDate(timesheet.weekStartDate)}
            </p>
            <p className="mt-0.5 text-xs text-slate-500">
              {timesheet.entryCount} {timesheet.entryCount === 1 ? 'entry' : 'entries'} ·{' '}
              {timesheet.totalHours} hrs ({timesheet.billableHours} billable)
            </p>
          </div>
          <Badge tone={STATUS_TONES[timesheet.status]}>{STATUS_LABELS[timesheet.status]}</Badge>
        </Link>
      )}
    </SectionCard>
  );
}

/** Submitted timesheets awaiting the current member's approval. */
function MyTimesheetsToApproveCard({ memberId }: { memberId: string }): JSX.Element {
  const query = useTimesheets({
    page: 1,
    pageSize: 50,
    approverId: memberId,
    status: 'submitted',
    sortBy: 'weekStartDate',
    sortDir: 'asc',
  });
  const membersQuery = useMemberOptions();
  const memberNames = useMemo(() => {
    const map = new Map<string, string>();
    for (const m of membersQuery.data ?? []) map.set(m.id, m.displayName);
    return map;
  }, [membersQuery.data]);
  const timesheets = query.data?.data ?? [];

  return (
    <SectionCard
      title="Timesheets to approve"
      description="Submitted weeks awaiting your approval"
      className="lg:col-span-2"
      action={
        <Link to="/approvals" className="text-xs font-medium text-brand-600 hover:text-brand-800">
          All approvals
        </Link>
      }
    >
      <div className="overflow-x-auto">
        <table className="w-full text-left text-sm">
          <tbody className="divide-y divide-slate-100">
            <DataState
              colSpan={3}
              isLoading={query.isLoading}
              isError={query.isError}
              error={query.error}
              onRetry={() => void query.refetch()}
              isEmpty={timesheets.length === 0}
              emptyTitle="Nothing to approve"
              emptyDescription="No submitted timesheets are waiting on you."
            >
              {timesheets.map((ts) => (
                <tr key={ts.id}>
                  <td className="py-2 pr-3 align-top">
                    <Link
                      to={`/timesheets/${ts.id}`}
                      className="font-medium text-brand-700 hover:text-brand-900"
                    >
                      Week of {formatDate(ts.weekStartDate)}
                    </Link>
                    <p className="text-xs text-slate-500">
                      {memberNames.get(ts.memberId) ?? 'Member'}
                    </p>
                  </td>
                  <td className="py-2 pr-3 align-top tabular-nums text-slate-600">
                    {ts.totalHours} hrs ({ts.billableHours} billable)
                  </td>
                  <td className="py-2 align-top">
                    <Badge tone={STATUS_TONES[ts.status]}>{STATUS_LABELS[ts.status]}</Badge>
                  </td>
                </tr>
              ))}
            </DataState>
          </tbody>
        </table>
      </div>
    </SectionCard>
  );
}

/** Open tickets assigned to the current member. */
function MyTicketsCard({ memberId }: { memberId: string }): JSX.Element {
  const navigate = useNavigate();
  const query = useTickets({
    page: 1,
    pageSize: 50,
    assigneeMemberId: memberId,
    open: true,
    sortBy: 'number',
    sortDir: 'desc',
  });
  // Hide charge-code tickets, which all live under project 461.
  const tickets = (query.data?.data ?? []).filter(
    (ticket) => ticket.projectNumber !== CHARGE_CODE_PROJECT_NUMBER,
  );

  return (
    <SectionCard
      title="My open tickets"
      description="Tickets assigned to you that are not closed"
      action={
        <Link to="/tickets" className="text-xs font-medium text-brand-600 hover:text-brand-800">
          All tickets
        </Link>
      }
    >
      <div className="overflow-x-auto">
        <table className="w-full text-left text-sm">
          <tbody className="divide-y divide-slate-100">
            <DataState
              colSpan={4}
              isLoading={query.isLoading}
              isError={query.isError}
              error={query.error}
              onRetry={() => void query.refetch()}
              isEmpty={tickets.length === 0}
              emptyTitle="No open tickets"
              emptyDescription="You have no open tickets assigned to you."
            >
              {tickets.map((ticket) => (
                <tr
                  key={ticket.id}
                  onClick={() => navigate(`/tickets?open=${ticket.id}`)}
                  className="cursor-pointer hover:bg-slate-50"
                >
                  <td className="py-2 pr-3 align-top tabular-nums text-slate-500">
                    {ticketDisplayId(ticket)}
                  </td>
                  <td className="py-2 pr-3 align-top">
                    <p className="font-medium text-slate-900">{ticket.title}</p>
                    <p className="text-xs text-slate-500">{ticket.customerName ?? '—'}</p>
                  </td>
                  <td className="py-2 pr-3 align-top">
                    <Badge tone={ticket.origin === 'project' ? 'green' : 'gray'}>
                      {ticket.origin === 'project' ? 'Project' : 'Support'}
                    </Badge>
                  </td>
                  <td className="py-2 pr-3 align-top">
                    {ticket.statusName ? (
                      <Badge tone="blue">{ticket.statusName}</Badge>
                    ) : (
                      <span className="text-slate-400">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </DataState>
          </tbody>
        </table>
      </div>
    </SectionCard>
  );
}

/** Open projects the current member manages. */
function MyProjectsCard({ memberId }: { memberId: string }): JSX.Element {
  const query = useProjects({
    page: 1,
    pageSize: 50,
    projectManagerId: memberId,
    status: 'active',
    sortBy: 'number',
    sortDir: 'desc',
  });
  const projects = query.data?.data ?? [];

  return (
    <SectionCard
      title="My open projects"
      description="Active projects you manage"
      action={
        <Link to="/projects" className="text-xs font-medium text-brand-600 hover:text-brand-800">
          All projects
        </Link>
      }
    >
      <div className="overflow-x-auto">
        <table className="w-full text-left text-sm">
          <tbody className="divide-y divide-slate-100">
            <DataState
              colSpan={3}
              isLoading={query.isLoading}
              isError={query.isError}
              error={query.error}
              onRetry={() => void query.refetch()}
              isEmpty={projects.length === 0}
              emptyTitle="No projects"
              emptyDescription="You do not manage any active projects."
            >
              {projects.map((project) => (
                <tr key={project.id}>
                  <td className="py-2 pr-3 align-top tabular-nums text-slate-500">
                    #{project.number}
                  </td>
                  <td className="py-2 pr-3 align-top">
                    <Link
                      to={`/projects/${project.id}`}
                      className="font-medium text-brand-700 hover:text-brand-900"
                    >
                      {project.name}
                    </Link>
                    <p className="text-xs text-slate-500">{project.customerName}</p>
                  </td>
                  <td className="py-2 align-top tabular-nums text-slate-600">
                    {project.actualHours} / {project.budgetHours} hrs
                  </td>
                </tr>
              ))}
            </DataState>
          </tbody>
        </table>
      </div>
    </SectionCard>
  );
}

/** Active projects where the current member is assigned to at least one ticket. */
function MyAssignedProjectsCard({ memberId }: { memberId: string }): JSX.Element {
  const query = useProjects({
    page: 1,
    pageSize: 50,
    assignedToMemberId: memberId,
    status: 'active',
    sortBy: 'number',
    sortDir: 'desc',
  });
  const projects = query.data?.data ?? [];

  return (
    <SectionCard
      title="My assigned projects"
      description="Active projects where you're assigned to a ticket"
      action={
        <Link to="/projects" className="text-xs font-medium text-brand-600 hover:text-brand-800">
          All projects
        </Link>
      }
    >
      <div className="overflow-x-auto">
        <table className="w-full text-left text-sm">
          <tbody className="divide-y divide-slate-100">
            <DataState
              colSpan={3}
              isLoading={query.isLoading}
              isError={query.isError}
              error={query.error}
              onRetry={() => void query.refetch()}
              isEmpty={projects.length === 0}
              emptyTitle="No assigned projects"
              emptyDescription="You are not assigned to a ticket on any active project."
            >
              {projects.map((project) => (
                <tr key={project.id}>
                  <td className="py-2 pr-3 align-top tabular-nums text-slate-500">
                    #{project.number}
                  </td>
                  <td className="py-2 pr-3 align-top">
                    <Link
                      to={`/projects/${project.id}`}
                      className="font-medium text-brand-700 hover:text-brand-900"
                    >
                      {project.name}
                    </Link>
                    <p className="text-xs text-slate-500">{project.customerName}</p>
                  </td>
                  <td className="py-2 align-top tabular-nums text-slate-600">
                    {project.actualHours} / {project.budgetHours} hrs
                  </td>
                </tr>
              ))}
            </DataState>
          </tbody>
        </table>
      </div>
    </SectionCard>
  );
}

/** Opportunities the current member owns as sales rep. */
function MyOpportunitiesCard({ memberId }: { memberId: string }): JSX.Element {
  const navigate = useNavigate();
  const query = useOpportunities({
    page: 1,
    pageSize: 50,
    salesRepId: memberId,
    sortBy: 'closeDate',
    sortDir: 'asc',
  });
  // Only show opportunities that are still in play: open or order approved.
  const opportunities = (query.data?.data ?? []).filter(
    (opp) => opp.status === 'open' || opp.status === 'order_approved',
  );

  return (
    <SectionCard
      title="My opportunities"
      description="Opportunities where you are the sales rep"
      action={
        <Link
          to="/sales/opportunities"
          className="text-xs font-medium text-brand-600 hover:text-brand-800"
        >
          All opportunities
        </Link>
      }
    >
      <div className="overflow-x-auto">
        <table className="w-full text-left text-sm">
          <tbody className="divide-y divide-slate-100">
            <DataState
              colSpan={4}
              isLoading={query.isLoading}
              isError={query.isError}
              error={query.error}
              onRetry={() => void query.refetch()}
              isEmpty={opportunities.length === 0}
              emptyTitle="No opportunities"
              emptyDescription="You are not the sales rep on any opportunities."
            >
              {opportunities.map((opp) => (
                <tr
                  key={opp.id}
                  onClick={() => navigate(`/sales/opportunities?open=${opp.id}`)}
                  className="cursor-pointer hover:bg-slate-50"
                >
                  <td className="py-2 pr-3 align-top font-medium text-slate-900">{opp.name}</td>
                  <td className="py-2 pr-3 align-top">
                    <Badge tone={OPPORTUNITY_STAGE_TONES[opp.stage]}>
                      {OPPORTUNITY_STAGE_LABELS[opp.stage]}
                    </Badge>
                  </td>
                  <td className="py-2 pr-3 align-top text-slate-600">
                    {OPPORTUNITY_STATUS_LABELS[opp.status]}
                  </td>
                  <td className="py-2 align-top tabular-nums text-slate-600">
                    {opp.closeDate ? formatDate(opp.closeDate) : '—'}
                  </td>
                </tr>
              ))}
            </DataState>
          </tbody>
        </table>
      </div>
    </SectionCard>
  );
}

export function DashboardPage(): JSX.Element {
  const { principal } = useAuth();
  const firstName = principal?.displayName.split(/\s+/)[0] ?? 'there';
  const memberId = principal?.type === 'member' ? principal.id : null;

  // Sections are driven by the member's FUNCTIONAL-ROLE flags (not RBAC permissions).
  const isTimesheetUser = principal?.isTimesheetUser ?? false;
  const isTimeApprover = principal?.isTimeApprover ?? false;
  const isProjectManager = principal?.isProjectManager ?? false;
  const isSalesRep = principal?.isSalesRep ?? false;
  const isDelivery = (principal?.isTechnical ?? false) || (principal?.isFunctional ?? false);

  const hasAnySection =
    isTimesheetUser || isTimeApprover || isProjectManager || isSalesRep || isDelivery;

  return (
    <>
      <PageHeader title={`Welcome back, ${firstName}`} description="Your work at a glance." />

      {memberId ? (
        hasAnySection ? (
          <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
            {isTimesheetUser ? <MyTimesheetCard memberId={memberId} /> : null}
            {isTimeApprover ? <MyTimesheetsToApproveCard memberId={memberId} /> : null}
            {isProjectManager ? <MyProjectsCard memberId={memberId} /> : null}
            {isSalesRep ? <MyOpportunitiesCard memberId={memberId} /> : null}
            {isDelivery ? <MyTicketsCard memberId={memberId} /> : null}
            {isDelivery ? <MyAssignedProjectsCard memberId={memberId} /> : null}
          </div>
        ) : (
          <p className="text-sm text-slate-500">
            No work sections are configured for your account yet.
          </p>
        )
      ) : (
        <p className="text-sm text-slate-500">Sign in with a member account to see your work.</p>
      )}
    </>
  );
}

